import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Treasury.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TREASURY_BG = "https://customer-assets.emergentagent.com/job_page-builder-225/artifacts/vj5gkupq_Treasury.png";

// Token icons/colors
const TOKEN_CONFIG = {
  ARCO: { color: '#d4b978', icon: '🌳' },
  POL: { color: '#8247e5', icon: '⬡' },
  WBTC: { color: '#f7931a', icon: '₿' },
  WETH: { color: '#627eea', icon: 'Ξ' },
  USDC: { color: '#2775ca', icon: '$' },
  USDT: { color: '#26a17b', icon: '$' }
};

function Treasury({ user, token }) {
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [balances, setBalances] = useState({});
  const [walletAddress, setWalletAddress] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [donations, setDonations] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Modal states
  const [showRecordTransaction, setShowRecordTransaction] = useState(false);
  const [showRecordDonation, setShowRecordDonation] = useState(false);
  
  // Form states
  const [txForm, setTxForm] = useState({ amount: '', token: 'ARCO', recipient: '', purpose: '', tx_hash: '', notes: '' });
  const [donationForm, setDonationForm] = useState({ amount: '', token: 'ARCO', from_address: '', tx_hash: '', donor_name: '' });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);
  
  // Check access
  const hasAccess = user && ['Founder', 'Aurel', 'Archivist'].includes(user.role);
  const canManage = user && ['Founder', 'Aurel'].includes(user.role);
  
  // Fetch data
  useEffect(() => {
    if (hasAccess) {
      fetchBalances();
      fetchTransactions();
      fetchDonations();
      fetchStats();
    }
  }, [hasAccess]);
  
  const fetchBalances = async () => {
    try {
      const response = await fetch(`${API_URL}/api/treasury/balances`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBalances(data.balances || {});
        setWalletAddress(data.wallet_address);
      }
    } catch (err) {
      console.error('Error fetching balances:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchTransactions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/treasury/transactions?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTransactions(data.transactions || []);
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
    }
  };
  
  const fetchDonations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/treasury/donations?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDonations(data.donations || []);
      }
    } catch (err) {
      console.error('Error fetching donations:', err);
    }
  };
  
  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/treasury/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };
  
  const recordTransaction = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/treasury/transactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...txForm,
          amount: parseFloat(txForm.amount)
        })
      });
      
      if (response.ok) {
        setFormSuccess('Transaction recorded successfully');
        setTxForm({ amount: '', token: 'ARCO', recipient: '', purpose: '', tx_hash: '', notes: '' });
        fetchTransactions();
        fetchStats();
        setTimeout(() => {
          setShowRecordTransaction(false);
          setFormSuccess(null);
        }, 1500);
      } else {
        const err = await response.json();
        setFormError(err.detail || 'Failed to record transaction');
      }
    } catch (err) {
      setFormError('Failed to record transaction');
    } finally {
      setFormLoading(false);
    }
  };
  
  const recordDonation = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/treasury/donations/record`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...donationForm,
          amount: parseFloat(donationForm.amount)
        })
      });
      
      if (response.ok) {
        setFormSuccess('Donation recorded successfully');
        setDonationForm({ amount: '', token: 'ARCO', from_address: '', tx_hash: '', donor_name: '' });
        fetchDonations();
        fetchStats();
        setTimeout(() => {
          setShowRecordDonation(false);
          setFormSuccess(null);
        }, 1500);
      } else {
        const err = await response.json();
        setFormError(err.detail || 'Failed to record donation');
      }
    } catch (err) {
      setFormError('Failed to record donation');
    } finally {
      setFormLoading(false);
    }
  };
  
  const formatBalance = (balance, token) => {
    if (balance === 0) return '0';
    if (balance < 0.0001) return '<0.0001';
    if (balance < 1) return balance.toFixed(6);
    if (balance < 1000) return balance.toFixed(4);
    if (balance < 1000000) return balance.toLocaleString(undefined, { maximumFractionDigits: 2 });
    return (balance / 1000000).toFixed(2) + 'M';
  };
  
  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Access denied
  if (!hasAccess) {
    return (
      <div className="treasury-container" style={{ backgroundImage: `url(${TREASURY_BG})` }}>
        <div className="treasury-overlay"></div>
        <div className="treasury-access-denied">
          <div className="vault-door-closed">
            <div className="vault-lock">🔒</div>
            <h2>Treasury Vault Sealed</h2>
            <p>Only Founders, Aurels, and Archivists may enter the Treasury.</p>
            <button className="back-btn" onClick={() => navigate('/')}>
              Return to Guild Hall
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="treasury-container" style={{ backgroundImage: `url(${TREASURY_BG})` }}>
      <div className="treasury-overlay"></div>
      
      {/* Header */}
      <header className="treasury-header">
        <button className="back-btn" onClick={() => navigate('/')} data-testid="back-btn">
          ← Return to Guild Hall
        </button>
        <div className="treasury-title">
          <h1>The Treasury Vault</h1>
          <p className="treasury-motto">"Value is Stewardship, Legacy is Eternal"</p>
        </div>
        <div className="treasury-wallet">
          <span className="wallet-label">Treasury Wallet</span>
          <a 
            href={`https://polygonscan.com/address/${walletAddress}`}
            target="_blank"
            rel="noopener noreferrer"
            className="wallet-address"
          >
            {formatAddress(walletAddress)}
          </a>
        </div>
      </header>
      
      {/* Navigation Tabs */}
      <nav className="treasury-nav">
        <button 
          className={`treasury-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Vault Holdings
        </button>
        <button 
          className={`treasury-tab ${activeTab === 'transactions' ? 'active' : ''}`}
          onClick={() => setActiveTab('transactions')}
        >
          Outflow Ledger
        </button>
        <button 
          className={`treasury-tab ${activeTab === 'donations' ? 'active' : ''}`}
          onClick={() => setActiveTab('donations')}
        >
          Donation Records
        </button>
      </nav>
      
      {/* Main Content */}
      <main className="treasury-content">
        {loading ? (
          <div className="treasury-loading">
            <div className="vault-spinner"></div>
            <p>Opening the Vault...</p>
          </div>
        ) : (
          <>
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="overview-panel">
                {/* Token Balances Grid */}
                <div className="balances-grid">
                  {Object.entries(balances).map(([tokenName, tokenData]) => {
                    const config = TOKEN_CONFIG[tokenName] || { color: '#d4b978', icon: '?' };
                    return (
                      <div 
                        key={tokenName} 
                        className="balance-card"
                        style={{ '--token-color': config.color }}
                      >
                        <div className="token-icon">{config.icon}</div>
                        <div className="token-info">
                          <span className="token-name">{tokenName}</span>
                          <span className="token-balance">
                            {formatBalance(tokenData.balance, tokenName)}
                          </span>
                        </div>
                        {tokenData.contract && (
                          <a 
                            href={`https://polygonscan.com/token/${tokenData.contract}?a=${walletAddress}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="view-explorer"
                            title="View on PolygonScan"
                          >
                            ↗
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {/* Stats Summary */}
                {stats && (
                  <div className="stats-summary">
                    <div className="stat-card">
                      <span className="stat-value">{stats.total_donations_count || 0}</span>
                      <span className="stat-label">Total Donations</span>
                    </div>
                    <div className="stat-card">
                      <span className="stat-value">{stats.total_transactions_count || 0}</span>
                      <span className="stat-label">Recorded Outflows</span>
                    </div>
                    {stats.donation_totals_by_token?.ARCO && (
                      <div className="stat-card">
                        <span className="stat-value">{formatBalance(stats.donation_totals_by_token.ARCO, 'ARCO')}</span>
                        <span className="stat-label">ARCO Donated</span>
                      </div>
                    )}
                    {stats.transaction_totals_by_token?.ARCO && (
                      <div className="stat-card">
                        <span className="stat-value">{formatBalance(stats.transaction_totals_by_token.ARCO, 'ARCO')}</span>
                        <span className="stat-label">ARCO Distributed</span>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Quick Actions */}
                {canManage && (
                  <div className="quick-actions">
                    <button 
                      className="action-btn record-tx"
                      onClick={() => setShowRecordTransaction(true)}
                    >
                      📝 Record Transaction
                    </button>
                    <button 
                      className="action-btn record-donation"
                      onClick={() => setShowRecordDonation(true)}
                    >
                      🎁 Record Donation
                    </button>
                    <button 
                      className="action-btn refresh"
                      onClick={() => {
                        fetchBalances();
                        fetchStats();
                      }}
                    >
                      🔄 Refresh Balances
                    </button>
                  </div>
                )}
              </div>
            )}
            
            {/* Transactions Tab */}
            {activeTab === 'transactions' && (
              <div className="transactions-panel">
                <div className="panel-header">
                  <h3>Outflow Ledger</h3>
                  <p>Record of all Treasury expenditures and distributions</p>
                  {canManage && (
                    <button 
                      className="action-btn record-tx"
                      onClick={() => setShowRecordTransaction(true)}
                    >
                      📝 Record Transaction
                    </button>
                  )}
                </div>
                
                {transactions.length === 0 ? (
                  <div className="empty-state">
                    <p>No transactions recorded yet.</p>
                  </div>
                ) : (
                  <div className="transactions-list">
                    {transactions.map((tx) => (
                      <div key={tx.id} className="transaction-row">
                        <div className="tx-icon" style={{ '--token-color': TOKEN_CONFIG[tx.token]?.color || '#d4b978' }}>
                          {TOKEN_CONFIG[tx.token]?.icon || '?'}
                        </div>
                        <div className="tx-details">
                          <span className="tx-purpose">{tx.purpose}</span>
                          <span className="tx-meta">
                            {tx.recipient && <span>To: {formatAddress(tx.recipient)}</span>}
                            {tx.recorded_by && <span>By: {tx.recorded_by}</span>}
                          </span>
                          {tx.notes && <span className="tx-notes">{tx.notes}</span>}
                        </div>
                        <div className="tx-amount">
                          <span className="amount">-{formatBalance(tx.amount, tx.token)} {tx.token}</span>
                          <span className="tx-date">{formatDate(tx.created_at)}</span>
                          {tx.tx_hash && (
                            <a 
                              href={`https://polygonscan.com/tx/${tx.tx_hash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="tx-link"
                            >
                              View TX ↗
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Donations Tab */}
            {activeTab === 'donations' && (
              <div className="donations-panel">
                <div className="panel-header">
                  <h3>Donation Records</h3>
                  <p>Contributions received to the Treasury</p>
                  {canManage && (
                    <button 
                      className="action-btn record-donation"
                      onClick={() => setShowRecordDonation(true)}
                    >
                      🎁 Record Donation
                    </button>
                  )}
                </div>
                
                {donations.length === 0 ? (
                  <div className="empty-state">
                    <p>No donations recorded yet.</p>
                  </div>
                ) : (
                  <div className="donations-list">
                    {donations.map((donation) => (
                      <div key={donation.id} className="donation-row">
                        <div className="donation-icon" style={{ '--token-color': TOKEN_CONFIG[donation.token]?.color || '#d4b978' }}>
                          {TOKEN_CONFIG[donation.token]?.icon || '?'}
                        </div>
                        <div className="donation-details">
                          <span className="donor-name">{donation.donor_name || 'Anonymous Donor'}</span>
                          <span className="donor-address">From: {formatAddress(donation.from_address)}</span>
                        </div>
                        <div className="donation-amount">
                          <span className="amount">+{formatBalance(donation.amount, donation.token)} {donation.token}</span>
                          <span className="donation-date">{formatDate(donation.timestamp)}</span>
                          {donation.tx_hash && (
                            <a 
                              href={`https://polygonscan.com/tx/${donation.tx_hash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="tx-link"
                            >
                              View TX ↗
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>
      
      {/* Record Transaction Modal */}
      {showRecordTransaction && (
        <div className="modal-overlay" onClick={() => setShowRecordTransaction(false)}>
          <div className="treasury-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowRecordTransaction(false)}>×</button>
            <h3>Record Treasury Transaction</h3>
            
            {formError && <div className="form-error">{formError}</div>}
            {formSuccess && <div className="form-success">{formSuccess}</div>}
            
            <form onSubmit={recordTransaction}>
              <div className="form-row">
                <div className="form-group">
                  <label>Amount *</label>
                  <input 
                    type="number"
                    step="any"
                    value={txForm.amount}
                    onChange={(e) => setTxForm({...txForm, amount: e.target.value})}
                    required
                    placeholder="0.00"
                  />
                </div>
                <div className="form-group">
                  <label>Token *</label>
                  <select 
                    value={txForm.token}
                    onChange={(e) => setTxForm({...txForm, token: e.target.value})}
                  >
                    <option value="ARCO">ARCO</option>
                    <option value="POL">POL</option>
                    <option value="WBTC">WBTC</option>
                    <option value="WETH">WETH</option>
                    <option value="USDC">USDC</option>
                    <option value="USDT">USDT</option>
                  </select>
                </div>
              </div>
              
              <div className="form-group">
                <label>Purpose *</label>
                <input 
                  type="text"
                  value={txForm.purpose}
                  onChange={(e) => setTxForm({...txForm, purpose: e.target.value})}
                  required
                  placeholder="e.g., Achievement rewards, Community event"
                />
              </div>
              
              <div className="form-group">
                <label>Recipient Address</label>
                <input 
                  type="text"
                  value={txForm.recipient}
                  onChange={(e) => setTxForm({...txForm, recipient: e.target.value})}
                  placeholder="0x..."
                />
              </div>
              
              <div className="form-group">
                <label>Transaction Hash</label>
                <input 
                  type="text"
                  value={txForm.tx_hash}
                  onChange={(e) => setTxForm({...txForm, tx_hash: e.target.value})}
                  placeholder="0x..."
                />
              </div>
              
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={txForm.notes}
                  onChange={(e) => setTxForm({...txForm, notes: e.target.value})}
                  placeholder="Additional details..."
                  rows={3}
                />
              </div>
              
              <button type="submit" className="submit-btn" disabled={formLoading}>
                {formLoading ? 'Recording...' : 'Record Transaction'}
              </button>
            </form>
          </div>
        </div>
      )}
      
      {/* Record Donation Modal */}
      {showRecordDonation && (
        <div className="modal-overlay" onClick={() => setShowRecordDonation(false)}>
          <div className="treasury-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowRecordDonation(false)}>×</button>
            <h3>Record Donation</h3>
            
            {formError && <div className="form-error">{formError}</div>}
            {formSuccess && <div className="form-success">{formSuccess}</div>}
            
            <form onSubmit={recordDonation}>
              <div className="form-row">
                <div className="form-group">
                  <label>Amount *</label>
                  <input 
                    type="number"
                    step="any"
                    value={donationForm.amount}
                    onChange={(e) => setDonationForm({...donationForm, amount: e.target.value})}
                    required
                    placeholder="0.00"
                  />
                </div>
                <div className="form-group">
                  <label>Token *</label>
                  <select 
                    value={donationForm.token}
                    onChange={(e) => setDonationForm({...donationForm, token: e.target.value})}
                  >
                    <option value="ARCO">ARCO</option>
                    <option value="POL">POL</option>
                    <option value="WBTC">WBTC</option>
                    <option value="WETH">WETH</option>
                    <option value="USDC">USDC</option>
                    <option value="USDT">USDT</option>
                  </select>
                </div>
              </div>
              
              <div className="form-group">
                <label>Donor Wallet Address *</label>
                <input 
                  type="text"
                  value={donationForm.from_address}
                  onChange={(e) => setDonationForm({...donationForm, from_address: e.target.value})}
                  required
                  placeholder="0x..."
                />
              </div>
              
              <div className="form-group">
                <label>Transaction Hash *</label>
                <input 
                  type="text"
                  value={donationForm.tx_hash}
                  onChange={(e) => setDonationForm({...donationForm, tx_hash: e.target.value})}
                  required
                  placeholder="0x..."
                />
              </div>
              
              <div className="form-group">
                <label>Donor Name (optional)</label>
                <input 
                  type="text"
                  value={donationForm.donor_name}
                  onChange={(e) => setDonationForm({...donationForm, donor_name: e.target.value})}
                  placeholder="Name for recognition"
                />
              </div>
              
              <button type="submit" className="submit-btn" disabled={formLoading}>
                {formLoading ? 'Recording...' : 'Record Donation'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Treasury;
